import { toast } from 'sonner';
import { useCanvasStore } from '@/stores/canvasStore';
import { watchExtensionJob } from '../jobs';
import { openBoard } from '../creative-scout/followAnalysis';

/** What pinterest-import answers with (202) once the run is claimed. */
export interface PinterestImportStarted {
  extension_job_id?: unknown;
  board_name?: unknown;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Follow an import until its pins are on the board, then refresh what the
 * user is looking at. Lives outside any component so it survives the panel
 * (or Partnr's chat) closing. Resolves true when the pins landed.
 */
export async function followPinterestImport(
  response: PinterestImportStarted,
  { intervalMs = 3000 }: { intervalMs?: number } = {},
): Promise<boolean> {
  const jobId = typeof response.extension_job_id === 'string' ? response.extension_job_id : null;
  const name = typeof response.board_name === 'string' && response.board_name ? response.board_name : 'Pinterest';
  if (!jobId) {
    toast.error('Could not start the Pinterest import. Please try again.');
    return false;
  }

  const progress = toast.loading(`Importing pins from "${name}". This takes a minute or two.`);
  const job = await watchExtensionJob(jobId, { intervalMs, timeoutMs: 7 * 60 * 1000 });
  toast.dismiss(progress);
  if (!job) {
    toast.info(`Still importing "${name}". The pins appear when you next open the board.`);
    return false;
  }
  if (job.status === 'failed') {
    toast.error(`Pinterest import failed: ${job.error_message || 'unknown error'}`);
    return false;
  }

  const result = job.result ?? {};
  const imported = Number(result.imported ?? 0);
  const boardName = typeof result.board_name === 'string' && result.board_name ? result.board_name : name;
  const folderId = typeof result.folder_board_id === 'string' ? result.folder_board_id : null;
  const targetId = typeof result.target_board_id === 'string' ? result.target_board_id : null;
  const itemIds = Array.isArray(result.item_ids) ? result.item_ids.filter((id): id is string => typeof id === 'string') : [];

  const store = useCanvasStore.getState();
  if (folderId) await store.fetchBoards();
  const current = useCanvasStore.getState().currentBoardId;
  if (current && (current === targetId || current === folderId)) await store.fetchItems(current);
  // Folder previews read allItems: refresh so the new folder shows its pins
  if (folderId) void store.fetchAllItems();

  const showPins = async () => {
    const { bringIntoView } = await import('@/lib/partnrActionExecutor');
    await bringIntoView(itemIds);
  };
  const action = folderId
    ? { label: 'Open folder', onClick: () => { void openBoard(folderId); } }
    : current === targetId && itemIds.length
      ? { label: 'Show', onClick: () => { void showPins(); } }
      : undefined;
  toast.success(`Imported ${plural(imported, 'pin')} from "${boardName}"${folderId ? ' into a new folder' : ''}.`, {
    duration: 12000,
    ...(action ? { action } : {}),
  });
  return true;
}
